import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sparkles,
  ShieldCheck,
  Clock,
  MapPin,
  Camera,
  Car,
  ChevronRight,
  Star,
  Phone,
  MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL, PHONE_SMS } from "@/lib/contact";
import CallbackForm from "@/components/CallbackForm";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BeforeAfter from "@/components/BeforeAfter";
import ReviewCard from "@/components/ReviewCard";

const HERO_IMG = "/hero-foam-wash.webp";

export default function Home() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/gallery", { params: { limit: 3 } }).then((r) => setGallery(r.data)).catch(() => {});
    api.get("/reviews", { params: { limit: 3 } }).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden cj-hero-radial" data-testid="hero-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 cj-fade-up">
            <div className="inline-flex items-center gap-2 cj-pill bg-[#ff6b00]/10 text-[#ff6b00] border border-[#ff6b00]/30">
              <Sparkles className="h-3 w-3" />
              Premium mobile detailing · AU
            </div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-100">
              Showroom shine.<br />
              <span className="text-[#ff6b00]">Delivered to your driveway.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              Prefer to talk it through? Call CJ direct for a quick quote and we'll book you in on the spot — or lock your own slot online in 60 seconds with a flat <span className="font-mono text-[#ff6b00] font-bold">$25</span> deposit.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={PHONE_TEL}
                data-testid="hero-cta-call-button"
                className="cj-btn-primary px-7 py-4 rounded-full inline-flex items-center gap-2 text-base"
              >
                <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
              </a>
              <Link
                to="/book"
                data-testid="hero-cta-book-button"
                className="cj-btn-ghost px-7 py-4 rounded-full inline-flex items-center gap-2"
              >
                Book online <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <a
              href={PHONE_SMS}
              data-testid="hero-cta-text-link"
              className="mt-3 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#ff6b00] transition"
            >
              <MessageSquare className="h-4 w-4" /> Prefer to text? Message CJ on {PHONE_DISPLAY}
            </a>

            <div className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400" data-testid="home-cancellation-policy">
              <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0" />
              <span>Free cancellation up to 24 h before — full <span className="text-slate-200 font-medium">$25</span> deposit refund.</span>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg">
              {[
                { k: "500+", v: "Cars Detailed" },
                { k: "4.9★", v: "Avg Rating" },
                { k: "48 hr", v: "Avg Slot" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display uppercase text-2xl sm:text-3xl font-extrabold text-slate-100">
                    {s.k}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative cj-fade-up" style={{ animationDelay: "140ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(255,107,0,0.3)]">
              <img src={HERO_IMG} alt="Mobile detailing" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f17] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-[#0b0f17]/80 backdrop-blur-xl border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[#ff6b00] flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-[#0b0f17]" strokeWidth={2.6} />
                  </div>
                  <div>
                    <div className="font-display uppercase font-bold text-slate-100">Competitive prices · Exceptional results</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">
                      Free cancellation 24 h+ before · full deposit refund
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="how-it-works">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">
          How it works
        </div>
        <h2 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-slate-100 max-w-2xl">
          Call CJ, or book yourself in.
        </h2>
        <p className="mt-3 text-slate-400 max-w-2xl">
          The fastest way to lock in a detail is a quick phone call — CJ will sort everything for you. Prefer to self-serve? Book online in four taps.
        </p>
        <div className="mt-10 grid gap-5 md:grid-cols-4">
          {[
            { i: Sparkles, t: "Pick a package", d: "Interior, Exterior, Full Detail or Ceramic Coating — priced to your car's size." },
            { i: Car, t: "Add your car", d: "Year, make, model + photos (we love detail — pun intended)." },
            { i: Clock, t: "Pick time + place", d: "Available slots at your doorstep, 7 days a week." },
            { i: ShieldCheck, t: "Lock the slot", d: "Pay a $25 Stripe deposit. Rest on completion." },
          ].map((s, i) => (
            <div key={s.t} className="cj-card p-6 relative overflow-hidden">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                Step {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-3 h-11 w-11 rounded-xl bg-[#ff6b00]/10 flex items-center justify-center">
                <s.i className="h-5 w-5 text-[#ff6b00]" strokeWidth={2.4} />
              </div>
              <div className="font-display uppercase font-bold text-xl text-slate-100 mt-4">
                {s.t}
              </div>
              <div className="text-sm text-slate-400 mt-2 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 cj-card p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-5 border border-[#ff6b00]/30 bg-[#ff6b00]/5" data-testid="home-call-banner">
          <div>
            <div className="font-display uppercase font-extrabold text-2xl text-slate-100">Rather just talk to a human?</div>
            <p className="text-slate-400 mt-1">Call CJ now for a quick quote and we'll book you straight in.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a href={PHONE_TEL} data-testid="home-call-banner-button" className="cj-btn-primary px-7 py-4 rounded-full inline-flex items-center gap-2 text-base">
              <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
            </a>
            <a href={PHONE_SMS} data-testid="home-call-banner-text" className="cj-btn-ghost px-6 py-4 rounded-full inline-flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Text us
            </a>
          </div>
        </div>
      </section>

      {/* REQUEST CALLBACK */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10" data-testid="home-callback">
        <CallbackForm />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="services-preview">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Packages</div>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-slate-100">
              Pick your shine
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="view-all-services-link"
            className="font-display uppercase text-sm font-bold text-[#ff6b00] hover:text-[#ff8a2a] inline-flex items-center gap-1"
          >
            View all services <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.slice(0, 4).map((s, i) => (
            <Link
              key={s.id}
              to={`/book?service=${s.id}`}
              data-testid={`home-service-card-${s.slug}`}
              className="cj-card group overflow-hidden flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt={s.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1a2234] to-[#121824]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f17] via-transparent to-transparent" />
                <div className="absolute top-3 left-3 cj-pill bg-[#0b0f17]/80 border border-white/10 text-slate-200">
                  <Clock className="h-3 w-3 text-[#ff6b00]" />
                  {s.duration_minutes}m
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="font-display uppercase font-bold text-xl text-slate-100 leading-tight">
                  {s.name}
                </div>
                <div className="mt-2 text-sm text-slate-400 line-clamp-2 flex-1">
                  {s.description}
                </div>
                <div className="mt-4 flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    From
                  </div>
                  <div className="font-display font-extrabold text-2xl text-[#ff6b00]">
                    ${s.price.toFixed(0)}
                    {s.quote_only && <span className="block font-mono text-[9px] uppercase tracking-wider text-cyan-300 text-right" data-testid={`home-quote-only-${s.slug}`}>quote only</span>}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BEFORE / AFTER SHOWCASE */}
      {gallery.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="home-gallery">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Real results</div>
              <h2 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-slate-100">
                Drag to see the difference
              </h2>
            </div>
            <Link to="/gallery" data-testid="view-gallery-link" className="font-display uppercase text-sm font-bold text-[#ff6b00] hover:text-[#ff8a2a] inline-flex items-center gap-1">
              Full gallery <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {gallery.map((it) => (
              <div key={it.id}>
                <BeforeAfter item={it} className="aspect-[4/3]" />
                <div className="mt-3 font-display uppercase font-bold text-slate-100">{it.caption}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">
                  {[it.vehicle_label, it.service_name].filter(Boolean).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="home-reviews">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">What customers say</div>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="relative overflow-hidden rounded-3xl border border-[#ff6b00]/30 bg-gradient-to-br from-[#1a1004] via-[#121824] to-[#121824] p-10 sm:p-14">
          <div className="relative z-10 flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">
                Ready to roll?
              </div>
              <h3 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100 max-w-xl">
                Your car's next shine is one tap away.
              </h3>
            </div>
            <Link
              to="/book"
              data-testid="band-cta-book-button"
              className="cj-btn-primary px-8 py-4 rounded-full inline-flex items-center gap-2"
            >
              Book Now <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-[#ff6b00]/20 blur-3xl" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
